/* =============================================================================
 * ServicoAplicacao - o acesso a API Prisma.
 *
 * A API nao e' REST: existe um dispatcher unico
 * (POST /executarfuncionalidade) e o nome da classe C# vai no corpo.
 *
 * Duas regras que definem tudo aqui:
 *  1. A resposta e' HTTP 200 SEMPRE, inclusive em erro de negocio. O erro real
 *     esta' em `data.Exception`. Nunca confiar em response.ok.
 *  2. Em /executarfuncionalidade o objeto vai CRU no corpo - o controller le'
 *     o Request.Body inteiro como o proprio JSON da funcionalidade. Os outros
 *     dois endpoints (/listarnegocios, /gerarrelatorio) mantiveram o envelope
 *     antigo, com o objeto serializado DENTRO de um campo string.
 *
 * Sem timeout de proposito: existem funcionalidades que legitimamente levam
 * minutos (relatorios, processamentos). Quem cancela e' o AbortController da
 * rota quando o usuario navega para fora.
 *
 * MODO OFFLINE. Este e' o unico ponto do app que sabe que ele existe: nenhuma
 * pagina muda por causa dele. A ordem e' sempre a mesma - primeiro pergunta ao
 * aparelho, so' depois vai a' rede -, e na volta a resposta que veio da rede e'
 * guardada quando vale a pena. Ver shared/offline/offline.js.
 * ========================================================================== */

import { config } from '@shared/lib/config.js';
import { paraJsonPrisma } from '@shared/lib/serializar.js';
import { ErroApi, ErroRede, ErroOffline, ErroTokenExpirado, ehErroDeToken } from '@shared/lib/erros.js';
import { paraData } from '@shared/lib/valores.js';
import { sessao, encerrar } from '@shared/sessao/sessao.js';
import { estaOffline, podeUsarOffline, notificarEstadoConexao } from '@shared/offline/conexao.js';
import { responderLocalmente, registrarResposta, deveBloquearChamada, ehGravacao } from '@shared/offline/offline.js';
import { montarSolicitacao } from './solicitacao.js';

//Preenchido por api/token.js no boot. Fica como injecao para o cliente nao
//precisar importar o modulo de token (que importa o cliente de volta).
let renovarToken = null;
export function registrarRenovadorDeToken(fn) {
    renovarToken = fn;
}

function cabecalhos(usarToken) {
    const h = {
        'Content-Type': 'application/json; charset=UTF-8',
        IdentificadorContaPrisma: config.identificadorContaPrisma,
    };
    if (usarToken && sessao.token) h.Authorization = `Bearer ${sessao.token}`;
    return h;
}

async function postar(caminho, corpo, { signal, semToken }) {
    let resposta;
    try {
        resposta = await fetch(`${config.urlServico}${caminho}`, {
            method: 'POST',
            signal,
            headers: cabecalhos(!semToken),
            body: corpo,
        });
    } catch (e) {
        //Cancelamento e' navegacao, nao falha - sobe puro para quem chamou ignorar.
        if (e.name === 'AbortError') throw e;
        //A requisicao nao chegou ao servidor: e' isto, e nao o HTTP feio logo
        //abaixo, que significa "este aparelho esta' sem internet".
        throw new ErroRede(caminho, e, { semResposta: true });
    }

    if (!resposta.ok) throw new ErroRede(caminho, new Error(`HTTP ${resposta.status}`));

    try {
        return await resposta.json();
    } catch (e) {
        throw new ErroRede(caminho, e);
    }
}

/**
 * @param {object} funcionalidade  { NomeFuncionalidade, ...parametros }
 * @param {object} [opcoes]        { signal, semToken, tentativa }
 */
export async function executarFuncionalidade(funcionalidade, opcoes = {}) {
    const { signal, semToken = false, tentativa = 0 } = opcoes;
    const nome = funcionalidade.NomeFuncionalidade;

    //===== modo offline: a decisao vem antes de qualquer outra coisa =====
    //Antes da renovacao de token de proposito: sem rede, renovar so' levaria a
    //sessao a cair. `estaOffline` so' e' verdade para quem ativou o recurso - sem
    //esse consentimento a chamada segue para a rede e falha normalmente.
    if (estaOffline()) {
        const local = await responderLocalmente(funcionalidade);
        if (local) return local;
        //Insistir na rede que ja' se sabe ausente so' faria a tela esperar a
        //falha. A excecao e' a sonda de tempos em tempos, que e' como o app
        //descobre que a internet voltou (ver conexao.js#permitirSondagem).
        if (deveBloquearChamada(nome)) throw new ErroOffline(nome, { gravacao: ehGravacao(nome) });
    }

    //Renovacao proativa: se o token ja' venceu, renova antes de gastar a chamada.
    if (!semToken && sessao.token && renovarToken && !tokenAindaValido()) {
        await renovarToken();
    }

    //Corpo cru, sem envelope: o controller le' o Request.Body inteiro. Continua
    //sendo paraJsonPrisma e nao JSON.stringify - e' ele que emite as datas no
    //formato do backend.
    const corpoFuncionalidade = { ...funcionalidade, Solicitacao: montarSolicitacao(nome) };
    const corpo = paraJsonPrisma(corpoFuncionalidade);

    let dados;
    try {
        dados = await postar('/executarfuncionalidadeprisma', corpo, { signal, semToken });
    } catch (erro) {
        //A rede caiu no meio: o estado e' marcado SEMPRE, com ou sem recurso
        //offline - e' ele que acende a faixa "sem conexão" da casca. Com o
        //recurso ligado ainda ha' uma chance de o aparelho responder.
        if (erro instanceof ErroRede && erro.semResposta) {
            notificarEstadoConexao('offline');
            if (podeUsarOffline()) {
                const local = await responderLocalmente(funcionalidade);
                if (local) return local;
            }
        }
        throw erro;
    }

    //Chegou resposta: a internet esta' de pe' (inclusive quando a sonda passou).
    notificarEstadoConexao('online');

    if (dados?.Exception) {
        if (ehErroDeToken(dados.Exception)) {
            //Renovacao reativa: uma unica tentativa, e nunca para a propria
            //chamada de autenticacao (senao vira recursao infinita).
            if (tentativa === 0 && sessao.token && renovarToken) {
                await renovarToken({ forcar: true });
                return executarFuncionalidade(funcionalidade, { ...opcoes, tentativa: 1 });
            }
            encerrar({ motivo: 'expirada' });
            throw new ErroTokenExpirado(dados.Exception);
        }
        throw new ErroApi(dados.Exception, nome, dados.Solicitacao);
    }

    if (config.modoDesenvolvedor && dados?.Solicitacao) {
        console.debug(`[api] ${nome} — ${dados.Solicitacao.ExecucaoMilissegundos ?? '?'}ms`);
    }

    //Guarda para uso offline. Nao e' esperado de proposito: quem pediu o dado ja'
    //o tem em maos, e a gravacao no IndexedDB nao pode atrasar a tela.
    registrarResposta(funcionalidade, dados);
    return dados;
}

/**
 * Margem de 1 minuto para nao mandar um token que vence no meio da viagem.
 *
 * paraData e nao Date.parse: TokenExpiracao vem no mesmo formato de data do
 * resto da API e seria deslocado pelo fuso. Tem que usar o MESMO parser que
 * calculou deltaHoraServidor, senao a comparacao mistura dois fusos.
 */
export function tokenAindaValido(margemMs = 60_000) {
    if (!sessao.token || !sessao.tokenExpiracao) return false;
    const expira = paraData(sessao.tokenExpiracao);
    if (!expira) return false;
    const agoraNoServidor = Date.now() + (sessao.deltaHoraServidor || 0);
    return agoraNoServidor + margemMs < expira.getTime();
}

/**
 * Listagem generica de entidades.
 *
 * ATENCAO: `Criterios` sao fragmentos de SQL CRU interpolados no servidor. Este
 * app tem acesso anonimo, entao esta funcao NAO deve ser usada nas telas
 * publicas - ver docs/API-CONTRATO.md. Os seletores de categoria e fase usam o
 * que ObterTabCompeticaoDetalhe ja' devolve. Fica aqui apenas para /admin.
 */
export async function listarNegocios({ nomeNegocio, criterios = [], orderBy = '' }, opcoes = {}) {
    const argumentos = {
        NomeNegocio: nomeNegocio,
        Criterios: criterios,
        OrderBy: orderBy,
        TratarException: true,
        Solicitacao: montarSolicitacao(`ListarNegocios:${nomeNegocio}`),
    };
    //Envelope: este endpoint nao acompanhou a mudanca do /executarfuncionalidade.
    const corpo = JSON.stringify({ ArgumentosJson: paraJsonPrisma(argumentos) });
    const dados = await postar('/listarnegocios', corpo, opcoes);

    if (dados?.Exception) throw new ErroApi(dados.Exception, `ListarNegocios:${nomeNegocio}`, dados.Solicitacao);
    return dados?.Lista ?? [];
}

/** Relatorios (nominata em PDF). Retorna o objeto com FileBytes preenchido. */
export async function gerarRelatorio(relatorio, opcoes = {}) {
    //Envelope: este endpoint nao acompanhou a mudanca do /executarfuncionalidade.
    const corpo = JSON.stringify({
        RelatorioJson: paraJsonPrisma({ ...relatorio, Solicitacao: montarSolicitacao(relatorio.NomeRelatorio) }),
    });
    const dados = await postar('/gerarrelatorio', corpo, opcoes);

    if (dados?.Exception) throw new ErroApi(dados.Exception, relatorio.NomeRelatorio, dados.Solicitacao);
    return dados;
}
